import {
  Button,
  Callout,
  DATE_FORMAT,
  Icon,
  Link,
  Tag,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { differenceInMinutes } from 'date-fns';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';

import {
  hasJournalsError,
  haveSettingsAtLeastOneValidationError,
} from 'modules/accounting-integration/models';
import { AccountingBasePage } from 'modules/bookkeep/settings/accounting/components/AccountingBasePage';
import { AccountingBaseInput } from 'modules/bookkeep/settings/accounting/components/AccountingBasePage/components/AccountingBaseInput';
import { AccountingBaseSelect } from 'modules/bookkeep/settings/accounting/components/AccountingBasePage/components/AccountingBaseSelect';
import { BetaFeatureFeedbackPopover } from 'src/core/common/components/BetaFeatureNotice/BetaFeatureNotice/BetaFeatureFeedbackPopover';
import { useFeature } from 'src/core/common/hooks/useFeature';
import {
  type TGlobalFunctionTyped,
  useTranslation,
  type I18nKey,
} from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { LAST_SERVER_STATUS_REFRESH } from 'src/core/constants/storage';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { scrollToElement } from 'src/core/modules/inbox-invoices/utils/dom';
import { rejectUnexpectedValue } from 'src/core/utils/switchGuard';

import { AuthFieldsAndLinks } from './AuthFieldsAndLinks';
import styles from './AuthSection.module.css';
import { ChooseSubsidiaryModal } from './ChooseSubsidiaryModal';
import { ChooseTenantDialog } from './ChooseTenantDialog';
import { DefaultVendorsSection } from './DefaultVendorsSection';
import { ExportCurrencyField } from './ExportCurrencyField';
import { ExportValidationSection } from './ExportValidationSection';
import { FileBasedAccountingIntegrationSettings } from './FileBasedAccountingIntegrationSettings';
import {
  getConnectionStatus,
  type IntegrationStatus,
  getTenantChooserState,
  hasIntegrationFileBasedExport,
  getSubsidiaryChooserState,
  type NativeAccountingIntegration,
  isIntegrationsPlanEnabled,
  isIntegrationStatusWithIntegrationSupportingSupplierSync,
  type AccountingSoftware,
  isIntegrationStatusWithIntegration,
} from '../../../../../../integration/status';
import {
  getIconNameFromConnection,
  getTagVariantFromConnection as getTagVariantFromConnectionStatus,
  getTextFromConnection as getTextFromConnectionStatus,
} from '../../../../connection';
import { getAccountingIntegrationDetails } from '../../../../helper';
import { useBuildScopedConsentUrlMutation } from '../../../../hooks/useBuildScopedConsentUrlMutation';
import { useChooseSubsidiaryMutation } from '../../../../hooks/useChooseSubsidiaryMutation';
import { useChooseTenantMutation } from '../../../../hooks/useChooseTenantMutation';
import { useRefreshServerStatusFetcher } from '../../../../hooks/useRefreshServerStatusFetcher';

interface Props {
  status: IntegrationStatus;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AuthSection = ({ status }: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const company = useCompany();

  const [chooseTenant] = useChooseTenantMutation();
  const [chooseSubsidiary] = useChooseSubsidiaryMutation();
  const [buildScopedConsentUrl] = useBuildScopedConsentUrlMutation();

  const isNetsuiteEnterpriseEnabled = useFeature(
    FEATURES.NETSUITE_ACCOUNTING_INTEGRATION,
  );

  const isNetsuiteBetaEnabled = useFeature(FEATURES.TMP_NETSUITE_BETA);

  const isNativeIntegration =
    status.integration !== 'noIntegration' &&
    status.integration !== 'switchInProgress' &&
    !hasIntegrationFileBasedExport(status.integration);

  const connectionStatus = getConnectionStatus(status);
  const tenantChooserState = getTenantChooserState(status);
  const subsidiaryChooserState = getSubsidiaryChooserState(status);

  const [isOpen, setIsOpen] = useState(false);

  const hasOauth1ConnectionError =
    status.integration !== 'noIntegration' &&
    status.integration !== 'switchInProgress' &&
    status.authorization.kind === 'oauth1' &&
    status.authorization.status.status === 'connectionError';

  const handleChooseTenant = async (tenantId: string) => {
    if (
      status.integration !== 'noIntegration' &&
      status.integration !== 'switchInProgress' &&
      status.authorization.kind === 'oauth2ScopedOpenId'
    ) {
      return buildScopedConsentUrl(tenantId);
    }
    await chooseTenant({ tenantId });
  };

  const handleChooseSubsidiary = async (subsidiaryId: string) => {
    await chooseSubsidiary({ id: subsidiaryId });
  };

  const hasIntegrationErrors =
    company.type !== 'branch_expense_entity' &&
    'settingsValidation' in status &&
    haveSettingsAtLeastOneValidationError(status.settingsValidation);

  const integrationPlanEnabled =
    status.integration !== 'noIntegration' &&
    status.integration !== 'switchInProgress' &&
    isIntegrationsPlanEnabled({
      integrationName: status.integration,
      featureFlags: {
        netsuite: isNetsuiteEnterpriseEnabled || isNetsuiteBetaEnabled,
      },
    });

  return (
    <div
      id="accounting-software"
      data-testid="accounting-software"
      className="flex flex-col gap-24"
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-8">
          <h3 className="heading-l">{accountingSoftwareName}</h3>
          {isNativeIntegration && (
            <Tag
              iconName={getIconNameFromConnection(connectionStatus)}
              variant={getTagVariantFromConnectionStatus(connectionStatus)}
            >
              {t(getTextFromConnectionStatus(connectionStatus))}
            </Tag>
          )}
          {isIntegrationStatusWithIntegration(status) && (
            <div className="ml-auto flex space-x-8">
              <OpenBeta integration={status.integration} />
              <OpenBetaFeedback integration={status.integration} />
              {isNativeIntegration &&
                connectionStatus === 'connected' &&
                status.settingsRefresh.kind === 'canRefresh' && (
                  <LastRefreshedAt
                    date={status.settingsRefresh.lastRefreshedAt}
                  />
                )}
            </div>
          )}
        </div>
        {hasIntegrationFileBasedExport(status.integration) && (
          <div className="text-content-secondary-bg-secondary body-m">
            {t('bookkeep.integrations.settings.accountingSoftwareDescription')}
            &nbsp;|&nbsp;
            <Link
              href="https://helpcenter.spendesk.com/collections/2383252-integrations-with-accounting-software"
              target="_blank"
            >
              {t('misc.helpCenterArticle')}
            </Link>
          </div>
        )}

        <div className="text-content-secondary-bg-secondary body-m">
          {isNativeIntegration && connectionStatus !== 'needServerUp' && (
            <Trans
              values={{ integration: accountingSoftwareName }}
              i18nKey={
                connectionStatus === 'connected'
                  ? getStatusConnectedDescriptionI18nKey(status)
                  : getStatusNotConnectedDescriptionI18nKey(status)
              }
            >
              <Link
                href={getStatusConnectedDescriptionLink(status)}
                target="_blank"
                rel="noopener noreferrer"
              >
                this article
              </Link>
            </Trans>
          )}
          {hasIntegrationFileBasedExport(status.integration) &&
            t(getFileBasedDescriptionI18nKey(status))}
        </div>
      </div>

      <ChooseTenantDialog
        brandName={accountingSoftwareName}
        chooserState={tenantChooserState}
        onChooseTenant={handleChooseTenant}
      />
      {isNativeIntegration && (
        <ChooseSubsidiaryModal
          brandName={accountingSoftwareName}
          chooserState={subsidiaryChooserState}
          onChooseSubsidiary={handleChooseSubsidiary}
          integration={status.integration as NativeAccountingIntegration}
        />
      )}
      {connectionStatus === 'needServerUp' &&
        (status.integration === 'Sage100' ||
          status.integration === 'DatevRewe') && (
          <>
            <Callout
              title={t('bookkeep.integrations.chift.serverNeedsToBeUp.title')}
              variant="warning"
            >
              <p>
                {t('bookkeep.integrations.chift.serverNeedsToBeUp.content', {
                  integration: status.integration,
                })}
              </p>
              <NeedServerUpActions />
            </Callout>
          </>
        )}

      {(connectionStatus === 'connected' || !isNativeIntegration) &&
        hasIntegrationErrors &&
        hasJournalsError(status.settingsValidation) && (
          <Callout
            title={
              <Trans i18nKey="bookkeep.integrations.settings.journalErrors.globalHasJournalErrorsTitle">
                <span
                  onClick={() => {
                    scrollToElement('journals-section');
                  }}
                  className="cursor-pointer underline"
                >
                  -
                </span>
              </Trans>
            }
            variant="warning"
          />
        )}
      <div className="separator" />
      {(status.integration === 'noIntegration' ||
        status.integration === 'switchInProgress') && (
        <AccountingBasePage
          isDefaultOpen={status.integration === 'switchInProgress'}
          integrationStatus={status}
        />
      )}

      {isIntegrationStatusWithIntegration(status) && (
        <>
          <AccountingBaseInput
            value={status.integration}
            onClick={() => setIsOpen(true)}
          />
          <AccountingBaseSelect
            initialIntegrationStatus={status}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onClose={() => setIsOpen(false)}
          />
        </>
      )}

      {isIntegrationStatusWithIntegration(status) &&
        status.integration !== 'Datev' &&
        status.selectedTenantName && (
          <div className="flex items-center justify-between">
            <div>{t('bookkeep.integrations.settings.connectedTenant')}</div>
            <div className="font-medium">{status.selectedTenantName}</div>
          </div>
        )}

      {isIntegrationStatusWithIntegration(status) &&
        integrationPlanEnabled &&
        connectionStatus === 'connectionError' &&
        (hasOauth1ConnectionError ? (
          <OAuth1Errors integrationName={accountingSoftwareName} />
        ) : (
          <ShowConnectionError status={status} t={t} />
        ))}

      {isIntegrationStatusWithIntegration(status) &&
        !integrationPlanEnabled && (
          <ShowNoAccessError integration={status.integration} />
        )}

      {isIntegrationStatusWithIntegration(status) &&
        status.capabilities.exportValidation &&
        connectionStatus === 'connected' && <ExportValidationSection />}

      {isIntegrationStatusWithIntegration(status) &&
        integrationPlanEnabled &&
        isIntegrationStatusWithIntegrationSupportingSupplierSync(status) &&
        connectionStatus === 'connected' && (
          <DefaultVendorsSection integration={status.integration} />
        )}

      {!isNativeIntegration && <FileBasedAccountingIntegrationSettings />}

      {status.integration === 'Netsuite' &&
        connectionStatus === 'connected' && <ExportCurrencyField />}

      {isNativeIntegration && <AuthFieldsAndLinks status={status} />}
    </div>
  );
};

type OpenBetaProps = {
  integration: AccountingSoftware;
};

const OpenBeta = ({ integration }: OpenBetaProps) => {
  const { t } = useTranslation('global');

  if (!getAccountingIntegrationDetails(integration).isBeta) {
    return null;
  }

  return <Tag variant="purple">{t('bookkeep.integrations.openBeta')}</Tag>;
};

type OpenBetaFeedbackProps = {
  integration: AccountingSoftware;
};

const OpenBetaFeedback = ({ integration }: OpenBetaFeedbackProps) => {
  const { t } = useTranslation('global');

  const [isBetaFeedBackOpen, setIsBetaFeedBackOpen] = useState(false);

  const integrationInfo = getAccountingIntegrationDetails(integration);

  if (!integrationInfo.isBeta) {
    return null;
  }

  return (
    <div>
      <Link
        className="text-content-brand-default ml-8"
        href="#"
        onClick={() => setIsBetaFeedBackOpen(true)}
      >
        {t('bookkeep.integrations.giveFeedback')}
      </Link>
      {isBetaFeedBackOpen && (
        <BetaFeatureFeedbackPopover
          featureName={integrationInfo.betaFeature}
          feedbackTags={[integrationInfo.betaTag]}
          onClose={() => setIsBetaFeedBackOpen(false)}
          disableSwitchToPreviousVersion
          isFeedbackMandatory={false}
          isForceAbsolute
        />
      )}
    </div>
  );
};

const LastRefreshedAt = ({ date }: { date: string | undefined }) => {
  const { t, localeFormat } = useTranslation('global');
  return (
    <div className="text-content-secondary-bg-primary flex items-center">
      <Icon name="clock-outline" size="s" />
      <span className="body-s ml-8">
        {date
          ? t('bookkeep.integrations.settings.lastRefreshedAt', {
              date: localeFormat(new Date(date), DATE_FORMAT.LONG_WITH_TIME),
            })
          : t('bookkeep.integrations.settings.neverRefreshed')}
      </span>
    </div>
  );
};

const ShowNoAccessError = ({ integration }: { integration: string }) => {
  const { t } = useTranslation('global');
  return (
    <Callout
      variant="alert"
      title={t('bookkeep.integrations.settings.noIntegrationInPlan.title', {
        integration,
      })}
    >
      <div>
        <p className="body-m mb-8">
          {t(
            'bookkeep.integrations.settings.noIntegrationInPlan.descriptionTop',
            {
              integration,
            },
          )}
        </p>
        <Button
          variant="secondaryNeutral"
          text={t(
            'bookkeep.integrations.settings.noIntegrationInPlan.reachOut',
          )}
          onClick={() => window.Intercom('show')}
        />
      </div>
    </Callout>
  );
};

const OAuth1Errors = ({
  integrationName,
}: {
  integrationName: string | undefined;
}) => {
  const { t } = useTranslation('global');
  return (
    <Callout
      variant="alert"
      title={t('bookkeep.integrations.settings.oAuth1Errors.title', {
        integrationName,
      })}
    >
      <div>
        <p className="body-m mb-8">
          {t('bookkeep.integrations.settings.oAuth1Errors.descriptionTop', {
            integrationName,
          })}
        </p>
        <ul className={classNames(styles.oAuth1ErrorList, 'body-m')}>
          <li>{t('bookkeep.integrations.settings.oAuth1Errors.accountId')}</li>
          <li>{t('bookkeep.integrations.settings.oAuth1Errors.clientId')}</li>
          <li>
            {t('bookkeep.integrations.settings.oAuth1Errors.clientSecret')}
          </li>
        </ul>
        <p className="body-m mb-8">
          {t('bookkeep.integrations.settings.oAuth1Errors.descriptionBottom')}
        </p>

        <Button
          variant="secondaryNeutral"
          text={t('bookkeep.integrations.settings.oAuth1Errors.reachOut')}
          onClick={() => window.Intercom('show')}
        />
      </div>
    </Callout>
  );
};

function getStatusConnectedDescriptionI18nKey(
  status: IntegrationStatus,
): string {
  const { integration } = status;

  switch (integration) {
    case 'Xero':
      return 'bookkeep.integrations.xero.statusDescription';
    case 'Datev':
      return 'bookkeep.integrations.datev.statusConnectedDescription';
    case 'SpendeskAccounting':
      return 'spendeskAccounting';
    case 'SpendeskAccountingSingleEntry':
      return 'spendeskAccountingSingleEntry';
    case 'Netsuite':
      return 'bookkeep.integrations.netsuite.statusConnectedDescription';
    case 'Sage':
      return 'sage';
    case 'Cegid':
      return 'cegid';
    case 'Quickbooks':
      return 'bookkeep.integrations.quickbooks.statusConnectedDescription';
    case 'Sage100':
    case 'PublicApi':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'MicrosoftDynamics':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'DatevRewe':
      return 'bookkeep.integrations.chift.statusConnectedDescription';
    case 'noIntegration':
      return 'noIntegration';
    case 'switchInProgress':
      return 'switchInProgress';
    default:
      rejectUnexpectedValue('integration type', integration);
  }
}

function getStatusConnectedDescriptionLink(status: IntegrationStatus): string {
  const { integration } = status;

  switch (integration) {
    case 'Datev':
      return 'https://helpcenter.spendesk.com/articles/5588518';
    case 'Xero':
      return 'https://helpcenter.spendesk.com/articles/5511455';
    case 'Netsuite':
      return 'https://helpcenter.spendesk.com/articles/6899573';
    case 'Sage100':
      return 'https://helpcenter.spendesk.com/collections/9677966-sage100-sur-compta-2-0';
    case 'ACD':
      return 'https://helpcenter.spendesk.com/fr/collections/10760041-integration-native-avec-acd';
    case 'Odoo':
      return 'https://helpcenter.spendesk.com/en/articles/12157278-odoo-native-integration-overview';
    case 'SageIntacct':
      return 'https://helpcenter.spendesk.com/en/articles/12158707-sage-intacct-native-integration-overview';
    case 'MicrosoftDynamics':
      return 'https://helpcenter.spendesk.com/en/articles/12158651-ms-business-central-dynamics-365-native-integration-overview';
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
      return 'https://helpcenter.spendesk.com/en/articles/12158608-exact-online-native-integration-overview';
    default:
      return 'https://helpcenter.spendesk.com/collections/2341495';
  }
}

function getStatusNotConnectedDescriptionI18nKey(
  status: IntegrationStatus,
): string {
  const { integration } = status;

  switch (integration) {
    case 'Xero':
      return 'bookkeep.integrations.xero.statusDescription';
    case 'Datev':
      return 'bookkeep.integrations.datev.statusNotConnectedDescription';
    case 'SpendeskAccounting':
      return 'spendeskAccounting';
    case 'SpendeskAccountingSingleEntry':
      return 'spendeskAccountingSingleEntry';
    case 'Sage':
      return 'sage';
    case 'Cegid':
      return 'cegid';
    case 'Netsuite':
      return 'bookkeep.integrations.netsuite.statusNotConnectedDescription';
    case 'Quickbooks':
      return 'bookkeep.integrations.quickbooks.statusNotConnectedDescription';
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'MicrosoftDynamics':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'DatevRewe':
      return 'bookkeep.integrations.chift.statusNotConnectedDescription';
    case 'PublicApi':
    case 'noIntegration':
      return 'noIntegration';
    case 'switchInProgress':
      return 'switchInProgress';
    default:
      rejectUnexpectedValue('integration type', integration);
  }
}

function getFileBasedDescriptionI18nKey(status: IntegrationStatus): I18nKey {
  const { integration } = status;

  switch (integration) {
    case 'SpendeskAccounting':
      return 'bookkeep.integrations.doubleEntry.statusDescription';
    case 'SpendeskAccountingSingleEntry':
      return 'bookkeep.integrations.singleEntry.statusDescription';
    case 'Netsuite':
      return 'bookkeep.integrations.netsuite.statusDescription';
    case 'Sage':
      return 'bookkeep.integrations.sage.statusDescription';
    case 'Cegid':
      return 'bookkeep.integrations.cegid.statusDescription';
    case 'Xero':
      return 'bookkeep.integrations.xero.statusDescription';
    case 'Datev':
      return 'bookkeep.integrations.datev.statusConnectedDescription';
    case 'Quickbooks':
      return 'bookkeep.integrations.quickbooks.statusConnectedDescription';
    case 'Sage100':
    case 'PublicApi':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'MicrosoftDynamics':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'DatevRewe':
      return 'bookkeep.integrations.chift.statusConnectedDescription';
    case 'noIntegration':
    case 'switchInProgress':
      return 'bookkeep.integrations.noIntegration.integrationName';
    default:
      rejectUnexpectedValue('integration type', integration);
  }
}

function ShowConnectionError({
  t,
  status,
}: {
  t: TGlobalFunctionTyped;
  status: IntegrationStatus;
}) {
  const { accountingSoftwareName } = useAccountingSoftwareName();

  if (
    status.integration === 'Datev' &&
    status.authorization.kind === 'oauth2ScopedOpenId' &&
    status.authorization.status.status === 'connectionError' &&
    status.authorization.status.error === 'tokenUnauthorized'
  ) {
    return (
      <Callout
        variant="alert"
        title={
          <Trans i18nKey="bookkeep.integrations.settings.connectionError.datev.tokenUnauthorized">
            <a
              href="https://helpcenter.spendesk.com/en/articles/5588518-configure-datev-uo-for-spendesk-integration"
              target="_blank"
              rel="noopener noreferrer"
            >
              this article
            </a>
          </Trans>
        }
      />
    );
  }

  return (
    <Callout
      variant="alert"
      title={t('bookkeep.integrations.settings.connectionErrorWarning', {
        integrationName: accountingSoftwareName,
      })}
    />
  );
}

const NeedServerUpActions = () => {
  const { t } = useTranslation('global');
  const refreshServerStatus = useRefreshServerStatusFetcher();
  const [hasAskedToRefreshServerStatus, setHasAskedToRefreshServerStatus] =
    useState(!shouldAllowServerStatusRefresh());

  const handleServerStatusRefresh = async () => {
    await refreshServerStatus();
    setHasAskedToRefreshServerStatus(true);
    localStorage.setItem(LAST_SERVER_STATUS_REFRESH, new Date().toISOString());
  };

  return (
    <div className="mt-16 flex flex-row gap-8">
      <Button
        text={t('bookkeep.integrations.settings.refreshServerStatus')}
        variant="primaryWarning"
        onClick={handleServerStatusRefresh}
        isDisabled={hasAskedToRefreshServerStatus}
      />
    </div>
  );
};

const shouldAllowServerStatusRefresh = () => {
  const lastServerStatusRefreshDateTimeString = localStorage.getItem(
    LAST_SERVER_STATUS_REFRESH,
  );

  if (lastServerStatusRefreshDateTimeString) {
    const diffInMinutes = differenceInMinutes(
      new Date(),
      new Date(lastServerStatusRefreshDateTimeString),
    );
    return Math.abs(diffInMinutes) >= 1;
  }
  return true;
};
